// SỔ QUỸ — /cashfund (manager+).
// Tồn quỹ hiện tại (két tiền mặt / TK ngân hàng) + đặt số dư đầu kỳ +
// lập phiếu THU / CHI tự do (lương, điện nước, thu khác…) + danh sách phiếu.
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDownCircle, ArrowUpCircle, Banknote, Building2, Pencil, Trash2, Wallet } from "lucide-react";
import { apiGet, apiClient } from "@/lib/api/client";
import { translateApiError } from "@/lib/errors/error-map";
import { Button } from "@/components/ui/button";
import { MoneyInput } from "@/components/ui/money-input";
import { formatDateTime } from "@/lib/format/date";

interface Balance {
  method: "cash" | "bank";
  opening?: number;
  asOf?: string;
  in: number;
  out: number;
  balance: number;
  hasOpening: boolean;
}

interface Voucher {
  id: string;
  code: string;
  type: "receipt" | "payment";
  amount: string;
  method: string;
  category: string;
  counterparty: string;
  note: string;
  occurredAt: string;
  createdByEmail?: string | null;
}

const METHOD_LABEL: Record<string, string> = { cash: "Tiền mặt", bank_manual: "Chuyển khoản" };

const CATEGORIES: { value: string; label: string; types: ("receipt" | "payment")[] }[] = [
  { value: "luong", label: "Lương nhân viên", types: ["payment"] },
  { value: "dien_nuoc", label: "Điện / nước / internet", types: ["payment"] },
  { value: "thue_mat_bang", label: "Thuê mặt bằng", types: ["payment"] },
  { value: "van_chuyen", label: "Vận chuyển / ship", types: ["payment"] },
  { value: "van_phong", label: "Văn phòng phẩm / vật tư", types: ["payment"] },
  { value: "hoan_tien", label: "Hoàn tiền khách", types: ["payment"] },
  { value: "thu_khac", label: "Thu khác", types: ["receipt"] },
  { value: "khac", label: "Khác", types: ["receipt", "payment"] },
];

function catLabel(v: string) {
  return CATEGORIES.find((c) => c.value === v)?.label ?? (v || "—");
}

function fmtVND(n: number) {
  return new Intl.NumberFormat("vi-VN").format(Math.round(n)) + " ₫";
}

// ── Dialog lập / sửa phiếu thu chi ────────────────────────────────────────────
// editing có giá trị = chế độ SỬA (giữ nguyên loại + mã phiếu, PATCH thay POST).
function VoucherDialog({ type, editing, onClose }: { type: "receipt" | "payment"; editing?: Voucher | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState(editing ? String(Math.round(parseFloat(editing.amount) || 0)) : "");
  const [method, setMethod] = useState<"cash" | "bank_manual">(
    editing?.method === "bank_manual" ? "bank_manual" : "cash",
  );
  const [category, setCategory] = useState(
    editing?.category || (type === "receipt" ? "thu_khac" : "luong"),
  );
  const [counterparty, setCounterparty] = useState(editing?.counterparty ?? "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [occurredOn, setOccurredOn] = useState(editing ? editing.occurredAt.slice(0, 10) : "");
  const [error, setError] = useState("");

  const isReceipt = type === "receipt";
  const createMutation = useMutation({
    mutationFn: () => {
      const body = {
        type,
        amount: parseFloat(amount),
        method,
        category,
        counterparty,
        note,
        occurredOn: occurredOn || undefined,
      };
      return editing
        ? apiClient.patch(`/v1/cashfund/vouchers/${editing.id}`, body)
        : apiClient.post("/v1/cashfund/vouchers", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashfund"] });
      onClose();
    },
    onError: (e) => setError(translateApiError(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md space-y-3 rounded-lg border bg-background p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={`font-semibold ${isReceipt ? "text-green-600" : "text-destructive"}`}>
          {editing
            ? `Sửa phiếu ${editing.code}`
            : isReceipt ? "Lập phiếu THU" : "Lập phiếu CHI"}
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <MoneyInput value={amount} onChange={setAmount} placeholder="Số tiền" className="w-40" autoFocus />
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as "cash" | "bank_manual")}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          >
            <option value="cash">Tiền mặt</option>
            <option value="bank_manual">Chuyển khoản</option>
          </select>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          >
            {CATEGORIES.filter((c) => c.types.includes(type)).map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <input
          value={counterparty}
          onChange={(e) => setCounterparty(e.target.value)}
          placeholder={isReceipt ? "Người nộp tiền (tùy chọn)" : "Người nhận tiền (tùy chọn)"}
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
        />
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Ghi chú / nội dung (tùy chọn)"
          className="h-9 w-full rounded-md border bg-background px-2 text-sm"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          Ngày (bỏ trống = hôm nay):
          <input
            type="date"
            value={occurredOn}
            onChange={(e) => setOccurredOn(e.target.value)}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          />
        </label>
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Hủy</Button>
          <Button
            size="sm"
            disabled={createMutation.isPending || !amount || parseFloat(amount) <= 0}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending
              ? "Đang lưu…"
              : editing ? "Lưu thay đổi" : isReceipt ? "Ghi phiếu thu" : "Ghi phiếu chi"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Dialog đặt số dư đầu kỳ ───────────────────────────────────────────────────
function OpeningDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [method, setMethod] = useState<"cash" | "bank_manual">("cash");
  const [amount, setAmount] = useState("");
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState("");

  const saveMutation = useMutation({
    mutationFn: () =>
      apiClient.put("/v1/cashfund/opening", {
        method,
        amount: parseFloat(amount || "0"),
        asOf,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cashfund"] });
      onClose();
    },
    onError: (e) => setError(translateApiError(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md space-y-3 rounded-lg border bg-background p-4 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-semibold">Đặt số dư đầu kỳ</h2>
        <p className="text-sm text-muted-foreground">
          Đếm két / xem số dư TK tại <b>đầu ngày</b> được chọn rồi nhập vào đây. Tồn quỹ = số này
          + thu − chi từ ngày đó. Đặt lại sẽ GHI ĐÈ giá trị cũ của phương thức đó.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value as "cash" | "bank_manual")}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          >
            <option value="cash">Két tiền mặt</option>
            <option value="bank_manual">TK ngân hàng</option>
          </select>
          <MoneyInput value={amount} onChange={setAmount} placeholder="Số dư đầu kỳ" className="w-40" />
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="h-9 rounded-md border bg-background px-2 text-sm"
          />
        </div>
        {error && (
          <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Hủy</Button>
          <Button size="sm" disabled={saveMutation.isPending || !asOf} onClick={() => saveMutation.mutate()}>
            {saveMutation.isPending ? "Đang lưu…" : "Lưu số dư"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Trang chính ───────────────────────────────────────────────────────────────
export function CashfundPage() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<"receipt" | "payment" | "opening" | null>(null);
  const [editVoucher, setEditVoucher] = useState<Voucher | null>(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");

  const { data: balances = [] } = useQuery({
    queryKey: ["cashfund", "balance"],
    queryFn: () => apiGet<Balance[]>("/v1/cashfund/balance"),
  });

  const { data: vouchers = [] } = useQuery({
    queryKey: ["cashfund", "vouchers", typeFilter, methodFilter, q],
    queryFn: () =>
      apiGet<Voucher[]>("/v1/cashfund/vouchers", {
        type: typeFilter || undefined,
        method: methodFilter || undefined,
        q: q || undefined,
        limit: 100,
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/v1/cashfund/vouchers/${id}`),
    onSuccess: () => {
      setError("");
      qc.invalidateQueries({ queryKey: ["cashfund"] });
    },
    onError: (e) => setError(translateApiError(e)),
  });

  const cash = balances.find((b) => b.method === "cash");
  const bank = balances.find((b) => b.method === "bank");
  const totalBalance = (cash?.balance ?? 0) + (bank?.balance ?? 0);
  const anyOpeningMissing = balances.some((b) => !b.hasOpening);

  const CARDS = [
    { label: "Két tiền mặt", icon: <Banknote size={18} />, cls: "text-green-600", b: cash },
    { label: "TK ngân hàng (CK + QR)", icon: <Building2 size={18} />, cls: "text-blue-600", b: bank },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Sổ quỹ</h1>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setDialog("receipt")}>
            <ArrowDownCircle size={16} className="mr-1" /> Lập phiếu THU
          </Button>
          <Button size="sm" variant="destructive" onClick={() => setDialog("payment")}>
            <ArrowUpCircle size={16} className="mr-1" /> Lập phiếu CHI
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDialog("opening")}>
            <Pencil size={15} className="mr-1" /> Số dư đầu kỳ
          </Button>
        </div>
      </div>

      {/* Tồn quỹ */}
      <div className="grid gap-3 sm:grid-cols-3">
        {CARDS.map((c) => (
          <div key={c.label} className="rounded-xl border bg-card p-4 space-y-1">
            <div className={`flex items-center gap-2 text-sm font-medium ${c.cls}`}>
              {c.icon} {c.label}
            </div>
            <p className="text-2xl font-bold tabular-nums">{fmtVND(c.b?.balance ?? 0)}</p>
            <p className="text-xs text-muted-foreground">
              {c.b?.hasOpening ? (
                <>Đầu kỳ {fmtVND(c.b.opening ?? 0)} ({c.b.asOf?.split("-").reverse().join("/")}) · Thu {fmtVND(c.b.in)} · Chi {fmtVND(c.b.out)}</>
              ) : (
                <>Chưa đặt số dư đầu kỳ — đang tính thu − chi toàn bộ lịch sử</>
              )}
            </p>
          </div>
        ))}
        <div className="rounded-xl border bg-card p-4 space-y-1">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <Wallet size={18} /> Tổng tồn quỹ
          </div>
          <p className="text-2xl font-bold tabular-nums">{fmtVND(totalBalance)}</p>
          <p className="text-xs text-muted-foreground">Két tiền mặt + TK ngân hàng</p>
        </div>
      </div>

      {anyOpeningMissing && (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
          ⚠️ Chưa đặt <b>số dư đầu kỳ</b> cho {balances.filter((b) => !b.hasOpening).length === 2 ? "cả 2 quỹ" : "1 quỹ"} —
          số tồn đang là thu − chi từ trước tới nay, KHÔNG phải tiền thật trong két.
          Bấm nút <b>"Số dư đầu kỳ"</b>: đếm két hôm nay, nhập số + chọn ngày hôm nay là xong.
        </p>
      )}

      {/* Lọc + danh sách phiếu */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Tìm mã phiếu / người nhận / ghi chú…"
          className="h-9 w-64 rounded-md border bg-background px-2 text-sm"
        />
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Thu + Chi</option>
          <option value="receipt">Phiếu thu</option>
          <option value="payment">Phiếu chi</option>
        </select>
        <select value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm">
          <option value="">Mọi phương thức</option>
          <option value="cash">Tiền mặt</option>
          <option value="bank_manual">Chuyển khoản</option>
        </select>
      </div>

      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Mã phiếu</th>
              <th className="px-3 py-2 text-left">Loại</th>
              <th className="px-3 py-2 text-left">Thời gian</th>
              <th className="px-3 py-2 text-right">Số tiền</th>
              <th className="px-3 py-2 text-left">Phương thức</th>
              <th className="px-3 py-2 text-left">Lý do</th>
              <th className="px-3 py-2 text-left">Người nhận/nộp</th>
              <th className="px-3 py-2 text-left">Ghi chú</th>
              <th className="px-3 py-2 text-left">Người lập</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {vouchers.length === 0 && (
              <tr>
                <td colSpan={10} className="py-8 text-center text-muted-foreground">
                  Chưa có phiếu thu/chi nào. (Thu bán hàng và chi trả NCC được ghi tự động, không hiện ở đây.)
                </td>
              </tr>
            )}
            {vouchers.map((v) => (
              <tr key={v.id} className="border-b last:border-0 hover:bg-muted/30">
                <td className="px-3 py-2 font-mono text-xs">{v.code}</td>
                <td className="px-3 py-2">
                  {v.type === "receipt" ? (
                    <span className="rounded bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700 dark:bg-green-950/50 dark:text-green-400">Thu</span>
                  ) : (
                    <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:bg-red-950/50 dark:text-red-400">Chi</span>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(v.occurredAt)}</td>
                <td className={`px-3 py-2 text-right font-semibold tabular-nums ${v.type === "receipt" ? "text-green-600" : "text-destructive"}`}>
                  {v.type === "receipt" ? "+" : "−"}{fmtVND(parseFloat(v.amount) || 0)}
                </td>
                <td className="px-3 py-2">{METHOD_LABEL[v.method] ?? v.method}</td>
                <td className="px-3 py-2">{catLabel(v.category)}</td>
                <td className="px-3 py-2">{v.counterparty || "—"}</td>
                <td className="px-3 py-2 max-w-[220px] text-xs">{v.note || "—"}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{v.createdByEmail ?? "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    type="button"
                    title="Sửa phiếu"
                    onClick={() => setEditVoucher(v)}
                    className="mr-2 text-primary hover:opacity-70"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    type="button"
                    title="Xóa phiếu"
                    onClick={() => {
                      if (confirm(`Xóa phiếu ${v.code}? Tồn quỹ sẽ được tính lại.`)) {
                        deleteMutation.mutate(v.id);
                      }
                    }}
                    className="text-destructive hover:opacity-70"
                  >
                    <Trash2 size={15} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-muted-foreground">
        Sổ quỹ chỉ ghi khoản thu/chi TỰ DO. Thu bán hàng + thu nợ khách và chi trả NCC được hệ thống
        ghi tự động (xem tổng hợp tại Báo cáo → Quỹ tiền — tồn quỹ ở đây đã bao gồm tất cả).
      </p>

      {dialog === "receipt" && <VoucherDialog type="receipt" onClose={() => setDialog(null)} />}
      {dialog === "payment" && <VoucherDialog type="payment" onClose={() => setDialog(null)} />}
      {dialog === "opening" && <OpeningDialog onClose={() => setDialog(null)} />}
      {editVoucher && (
        <VoucherDialog
          key={editVoucher.id}
          type={editVoucher.type}
          editing={editVoucher}
          onClose={() => setEditVoucher(null)}
        />
      )}
    </div>
  );
}
